import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { farmModuleApi } from '../api/dairy';
import { ENABLED_MODULES } from '../config/farmConfig';

const FarmModuleContext = createContext(null);

/**
 * Provides farm module awareness to the entire app.
 * Seeds from the auth session for instant render, then syncs from the API
 * so the sidebar always reflects the current server state (e.g. after a
 * module subscription expires or is disabled).
 *
 * Usage:
 *   const { hasModule, activeModules } = useFarmModules();
 *   if (hasModule('DAIRY')) { ... }
 *
 * Also holds `viewModule`/`setViewModule` — the module currently shown in
 * module-aware UI (Dashboard tab, BottomNav switcher) — so those toggles
 * stay in sync with each other instead of each keeping separate state.
 */
export function FarmModuleProvider({ children }) {
  const { isLoggedIn, activeModules: sessionModules, updateActiveModules } = useAuth();

  // Seed from session so the sidebar renders immediately without flicker
  const [activeModules, setActiveModules] = useState(
    (sessionModules || ['POULTRY']).filter((m) => ENABLED_MODULES.has(m.toUpperCase()))
  );

  // Which module's data is currently on screen — shared so the Dashboard's
  // Poultry/Dairy tab and the mobile BottomNav's Poultry/Dairy switcher
  // always agree, instead of drifting as two independent toggles.
  const [viewModule, setViewModule] = useState(() =>
    activeModules.includes('POULTRY') ? 'POULTRY' : (activeModules[0] || 'POULTRY')
  );

  // Keep viewModule valid as activeModules changes (e.g. after the API sync
  // below finds the tenant no longer has the currently-viewed module).
  useEffect(() => {
    if (!Array.isArray(activeModules) || activeModules.length === 0) return;
    if (!activeModules.includes(viewModule)) {
      setViewModule(activeModules.includes('POULTRY') ? 'POULTRY' : activeModules[0]);
    }
  }, [activeModules]); // eslint-disable-line react-hooks/exhaustive-deps

  // On login, sync from session immediately — filter by build-time feature flag
  useEffect(() => {
    const filtered = (sessionModules || ['POULTRY']).filter((m) => ENABLED_MODULES.has(m.toUpperCase()));
    setActiveModules(filtered.length > 0 ? filtered : ['POULTRY'].filter((m) => ENABLED_MODULES.has(m)));
  }, [sessionModules]);

  // Fetch from API once on login to get the real current state
  useEffect(() => {
    if (!isLoggedIn) return;
    farmModuleApi.listModules()
      .then((mods) => {
        if (!Array.isArray(mods) || mods.length === 0) return;
        const current = mods
          .filter((m) => m.isActive && ENABLED_MODULES.has(m.moduleType?.toUpperCase()))
          .map((m) => m.moduleType);
        setActiveModules(current);
        updateActiveModules(current);
      })
      .catch(() => { /* non-fatal — session values remain */ });
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasModule = useCallback(
    (moduleType) => Array.isArray(activeModules) && activeModules.includes(moduleType.toUpperCase()),
    [activeModules]
  );

  const hasPoultry = hasModule('POULTRY');
  const hasDairy   = hasModule('DAIRY');
  const hasGoat    = hasModule('GOAT');

  const value = {
    activeModules,
    hasModule,
    hasPoultry,
    hasDairy,
    hasGoat,
    viewModule,
    setViewModule,
  };

  return <FarmModuleContext.Provider value={value}>{children}</FarmModuleContext.Provider>;
}

export function useFarmModules() {
  const ctx = useContext(FarmModuleContext);
  if (!ctx) throw new Error('useFarmModules must be used inside FarmModuleProvider');
  return ctx;
}
